/**
 * Upload: the snippet review screen from design/canvas/Main.src.html. Left, the zoom card: proxy player,
 * the whole clip's waveform coloured by sound region with scene cuts, ranges and free handles, then the
 * zoomed selection. Right, the snippet rows. Handles drag freely, an edge inside a word shows red, arrow
 * keys nudge one frame, Accept commits, Save indexes the batch.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useBlocker, useNavigate, useRouteContext, useSearch } from '@tanstack/react-router';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { AUDIO_RAMP, AUDIO_SELECT_MAX, IMPORTS_LIST_LIMIT, AUTOSAVE_DEBOUNCE_MS, DESCRIPTION_MAX_CHARS, HEADING_MAX_CHARS, PHRASE_KIND_LABELS, SAVED_SOUND_KIND, SNIPPET_KIND_LABELS, WORK_POLL_MS, analyseTimeLeft, rampColour, transcribesSpeech, type AnalyseParams, type PhraseKind, type SnippetKind } from '@studio/contracts';
import { AspectPeek, useAspectPeek } from '@/components/aspect-peek';
import { TopBar } from '@/components/shell';
import { TagAssetsModal } from '@/components/tag-assets-modal';
import { AutoPill } from '@/components/auto-marker';
import { Icon, Spark } from '@/components/icons';
import { RecentFiles } from '@/components/recent-files';
import { RecentModal } from '@/components/recent-modal';
import { RemixModal } from '@/components/remix-modal';
import { ManagedSelect } from '@/components/managed-select';
import { RowMenu } from '@/components/row-menu';
import { StarRating, ratingMenuItem } from '@/components/star-rating';
import { Tool, ToolDivider, ToolField, Toolbar } from '@/components/toolbar';
import { BlockStrip, MiniWave, Waveform } from '@/components/waveform';
import { useRatings } from '@/hooks/use-ratings';
import { clipAspect, fmtBytes, fmtTime } from '@/lib/format';
import { EDGE_INSET_MS, FRAME_WATCH_LEAD_MS, insideEdge } from '@/lib/playback';
import { SentBadge, useComposerActions } from '@/components/composer-actions';
import { queryClient, trpc } from '@/lib/trpc';

/** Colour of a switched-off row's swatch and wave. */
const OFF_ROW_COLOUR = '#d5d5da';
const SCENE_KIND_TIPS: Record<string, string> = {
  primary: 'highlight',
  secondary: 'supporting shot',
  passage: 'passage: a long take cut at a pause in the speech',
};

/** Audio row type badges: what kind of sound a line is, at a glance. */
const KIND_BADGES: Record<string, { icon: string; label: string }> = {
  voice: { icon: 'person', label: PHRASE_KIND_LABELS['voice']! },
  effect: { icon: 'sfx', label: PHRASE_KIND_LABELS['effect']! },
  ambient: { icon: 'jitter', label: PHRASE_KIND_LABELS['ambient']! },
  music: { icon: 'music', label: PHRASE_KIND_LABELS['music']! },
  mute: { icon: 'mute', label: PHRASE_KIND_LABELS['mute']! },
};
/** The order a reclassify menu offers the kinds in. */
const PHRASE_KINDS: PhraseKind[] = ['voice', 'ambient', 'effect', 'music', 'mute'];
/** The audio tab's legend: each kind's tint, so no sound row reads as switched off. */
const SOUND_LEGEND: Record<string, string> = { voice: 'Voice', ambient: 'Ambience', effect: 'Sound effect', music: 'Music', mute: 'Silence' };

/** Where a saved sound stands, as the legend and the row badges name it. */
const SOUND_STATUS_LABELS: Record<'library' | 'global', string> = { library: 'In library', global: 'Global' };

function SoundStatusIcon({ status }: { status: 'library' | 'global' }) {
  return <Icon name={status === 'global' ? 'globe' : 'bookmark'} />;
}

/** Enter saves an inline edit, as leaving the field does; Shift+Enter still breaks a line. */
function commitOnEnter(e: ReactKeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) {
  if (e.key !== 'Enter' || e.shiftKey) return;
  e.preventDefault();
  e.currentTarget.blur();
}

/** A row with no heading or classifier of its own is called by its kind. */
function rowKindLabel(kind: string): string {
  return SNIPPET_KIND_LABELS[kind as SnippetKind] ?? kind;
}

/** The bar never reads as empty once a stage has begun. */
const ANALYSE_PROGRESS_FLOOR = 0.03;

export function AnalysePage() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/_app/analyse' });
  const [assetId, setAssetId] = useState<string | null>(search.asset ?? null);
  const { me } = useRouteContext({ from: '/_app' });
  const isOwner = me.active?.role === 'owner';
  // Audio snippets being cut into the library: the clip re-reads itself until each one lands.
  const [savingSounds, setSavingSounds] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<string | null>(null);
  const [staged, setStaged] = useState<{ inMs: number; outMs: number } | null>(null);
  const [lastHandle, setLastHandle] = useState<'in' | 'out'>('out');
  const [playheadMs, setPlayheadMs] = useState<number | null>(null);
  const [dirty, setDirty] = useState(false);
  // Video scenes lead: the whole clip, cut where the picture changes, is the first thing to read.
  const [mediaTab, setMediaTab] = useState<'scenes' | 'audio' | 'highlights' | 'extras'>('scenes');
  /** The eye in the transport: everything beneath the play controls stays collapsed until asked for. */
  const [detailsOpen, setDetailsOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Only fuels Load next; the browsing home for clips is the Library now.
  const list = useQuery(trpc.assets.imports.queryOptions({ filter: 'all', limit: IMPORTS_LIST_LIMIT }));
  const composer = useComposerActions();
  const detail = useQuery(trpc.assets.detail.queryOptions({ assetId: assetId ?? '' }, {
    enabled: assetId !== null,
    refetchInterval: (q) => (q.state.data?.status === 'awaiting_upload'
      || (q.state.data?.phrases ?? []).some((p) => savingSounds.has(p.id) && p.status === 'described') ? WORK_POLL_MS : false),
    // Coming back to the tab shows where the analysis actually got to, not where it was when it left.
    refetchOnWindowFocus: true,
  }));

  const refresh = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: trpc.assets.imports.queryKey() }),
      assetId ? queryClient.invalidateQueries({ queryKey: trpc.assets.detail.queryKey({ assetId }) }) : Promise.resolve(),
    ]);
  }, [assetId]);
  const [saveResult, setSaveResult] = useState<{ indexed: number; skipped: { name: string; reason: string }[] } | null>(null);
  const editedAndRefresh = async () => {
    // Already-indexed assets autosave completely; only pre-index edits leave un-indexed work behind.
    if (detail.data && detail.data.status !== 'indexed') setDirty(true);
    await refresh();
  };
  const update = useMutation(trpc.assets.updateSnippet.mutationOptions({ onSuccess: editedAndRefresh }));
  const updatePhrase = useMutation(trpc.assets.updatePhrase.mutationOptions({ onSuccess: editedAndRefresh }));
  const saveSound = useMutation(trpc.assets.saveSound.mutationOptions({ onSuccess: refresh }));
  const removeSound = useMutation(trpc.assets.removeSound.mutationOptions({ onSuccess: refresh }));
  const shareSounds = useMutation(trpc.assets.shareSounds.mutationOptions({ onSuccess: refresh }));
  const nudge = useMutation(trpc.assets.nudgeSnippet.mutationOptions({ onSuccess: editedAndRefresh }));
  const addSnippet = useMutation(trpc.assets.addSnippet.mutationOptions({ onSuccess: editedAndRefresh }));
  const splitSnippet = useMutation(trpc.assets.splitSnippet.mutationOptions({ onSuccess: editedAndRefresh }));
  const mergeSnippets = useMutation(trpc.assets.mergeSnippets.mutationOptions({ onSuccess: editedAndRefresh }));
  const roles = useQuery(trpc.roles.list.queryOptions());
  const refreshRoles = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: trpc.roles.list.queryKey() }),
      refresh(),
    ]);
  };
  const addRole = useMutation(trpc.roles.add.mutationOptions({ onSuccess: refreshRoles }));
  const renameRole = useMutation(trpc.roles.rename.mutationOptions({ onSuccess: refreshRoles }));
  const removeRole = useMutation(trpc.roles.remove.mutationOptions({ onSuccess: refreshRoles }));
  const confirm = useMutation(trpc.assets.confirm.mutationOptions({
    onSuccess: async (data) => {
      setSaveResult({ indexed: data.indexed.length, skipped: data.skipped });
      setDirty(false);
      setAssetId(null);
      setSelected(null);
      setStaged(null);
      await refresh();
    },
  }));
  /** Tagging, on this clip, with the row selected there: the subclip, the audio snippet, or the whole clip. */
  const openInTagging = (row: { snippet?: string; phrase?: string } = {}) => {
    if (detail.data && confirmLeave()) void navigate({ to: '/tagging', search: { asset: detail.data.id, ...row } });
  };
  const confirmLeave = () => !dirty || window.confirm('This clip has edits that are not yet saved and indexed. Leave without Save and index?');
  useBlocker({ shouldBlockFn: () => !confirmLeave(), disabled: !dirty });

  // One file at a time, by choice: the Analyser stays empty until a clip is loaded from the Library.
  const openAsset = (id: string) => {
    if (id !== assetId && !confirmLeave()) return;
    setAssetId(id);
    setSelected(null);
    setStaged(null);
  };

  const d = detail.data;
  const durationMs = d?.durationMs ?? 1;
  // Spoken lines wear the earth-tone palette, deterministic by order, so rows and blocks agree.
  const phraseRows = useMemo(() => (d?.phrases ?? []).map((p, i, all) => ({ ...p, colour: rampColour(i, all.length, AUDIO_RAMP) })), [d?.phrases]);
  // Whole-clip scenes are index rows like highlights and extras: frame-exact, visual separation first,
  // then the brand's priorities deciding what reconnects and what folds. Every kind arrives coloured.
  const scenes = useMemo(() => (d?.snippets ?? []).filter((s) => s.kind === 'scene').map((s, i) => ({
    ...s,
    index: i + 1,
    colourNames: s.scene && s.scene.segmentIndex >= 0 ? d?.segments[s.scene.segmentIndex]?.colours ?? [] : [],
  })), [d]);
  // Highlights are the AI's picks and the rows drawn by hand; extras are frame-tight stand-outs (steady colours, still pack shots).
  const highlightRows = useMemo(() => (d?.snippets ?? []).filter((s) => s.kind === 'highlight'), [d?.snippets]);
  const extraRows = useMemo(() => (d?.snippets ?? []).filter((s) => s.kind === 'extra'), [d?.snippets]);
  // Every kept row carries its star rating, and the same choice heads its menu.
  const ratingTargets = useMemo(() => [
    ...(d?.snippets ?? []).filter((s) => !s.excluded).map((s) => ({ kind: 'snippet' as const, id: s.id })),
    ...(d?.phrases ?? []).filter((p) => !p.excluded).map((p) => ({ kind: 'phrase' as const, id: p.id })),
  ], [d?.snippets, d?.phrases]);
  const ratings = useRatings(ratingTargets);
  const starsOf = (kind: 'snippet' | 'phrase', id: string) => <StarRating rating={ratings.ratingOf({ kind, id })} onRate={(n) => ratings.rate({ kind, id }, n)} />;
  const rateItems = (kind: 'snippet' | 'phrase', id: string) => [ratingMenuItem(ratings.ratingOf({ kind, id }), (n) => ratings.rate({ kind, id }, n)), null];
  const snippet = d?.snippets.find((s) => s.id === selected) ?? null;
  const phrase = phraseRows.find((p) => p.id === selected) ?? null;
  // The zoom card adjusts whichever kind is selected: a video row (highlight, extra or scene) or a spoken line.
  const sel = snippet
    ? {
      id: snippet.id, inMs: snippet.inMs, outMs: snippet.outMs, colour: snippet.colour, kind: 'snippet' as const,
      label: snippet.kind === 'scene' ? `scene ${scenes.find((sc) => sc.id === snippet.id)?.index ?? ''}` : snippet.role ?? snippet.title ?? rowKindLabel(snippet.kind),
    }
    : phrase
      ? { id: phrase.id, inMs: phrase.startMs, outMs: phrase.endMs, colour: phrase.colour, label: 'spoken line', kind: 'phrase' as const }
      : null;
  const inMs = staged?.inMs ?? sel?.inMs ?? 0;
  const outMs = staged?.outMs ?? sel?.outMs ?? 0;
  const frameMs = d?.fps ? 1000 / d.fps : 40;
  // The row whose on/off switch is saving right now: its swatch shows a spinner until the list refreshes.
  const switching = update.isPending && update.variables?.excluded !== undefined ? update.variables.snippetId : null;
  const phraseSwitching = updatePhrase.isPending && updatePhrase.variables?.excluded !== undefined ? updatePhrase.variables.phraseId : null;
  // The audio legend names only the kinds this clip has, in the legend's own order.
  const soundKinds = Object.keys(SOUND_LEGEND).filter((k) => phraseRows.some((p) => p.kind === k));
  const soundStatuses = (['library', 'global'] as const).filter((st) => phraseRows.some((p) => p.status === st));
  /** Ticked rows when this row is one of several ticked, else this row alone: what a row menu's action acts on. */
  const actsOn = (p: (typeof phraseRows)[number]) => (mergeSel.size > 1 && mergeSel.has(p.id) ? phraseRows.filter((r) => mergeSel.has(r.id)) : [p]);
  /**
   * Global means the house library every tenant reads; a described row is cut straight into it. A
   * voiceover is a brand's own creator's voice and never goes global (Dax, 2026-09-29).
   */
  const promotable = (p: (typeof phraseRows)[number]) => !p.excluded && p.kind !== 'voice' && p.status !== 'global' && (p.status === 'library' || SAVED_SOUND_KIND[p.kind as PhraseKind] !== undefined);
  const promote = async (rows: (typeof phraseRows)[number][]) => {
    const ids = rows.filter(promotable).map((r) => r.id);
    setSavingSounds((prev) => new Set([...prev, ...rows.filter((r) => r.status === 'described' && ids.includes(r.id)).map((r) => r.id)]));
    for (let i = 0; i < ids.length; i += AUDIO_SELECT_MAX) await shareSounds.mutateAsync({ phraseIds: ids.slice(i, i + AUDIO_SELECT_MAX), global: true });
  };
  /** A sound's library actions and its reclassification, for the row menu. */
  const soundMenu = (p: (typeof phraseRows)[number]) => {
    const saveable = SAVED_SOUND_KIND[p.kind as PhraseKind] !== undefined;
    const saving = savingSounds.has(p.id) && p.status === 'described';
    const targets = actsOn(p).filter(promotable);
    const promoteItem = isOwner && promotable(p) ? [{
      label: targets.length > 1 ? `Promote ${targets.length} ticked to global` : `Promote to global ${(KIND_BADGES[p.kind]?.label ?? p.kind).toLowerCase()}`,
      disabled: saving || shareSounds.isPending,
      onClick: () => { void promote(targets); },
    }] : [];
    const library = p.status === 'described'
      ? [...(saveable ? [{ label: saving ? 'Saving to the library…' : 'Save to the library', disabled: saving, onClick: () => {
        setSavingSounds((prev) => new Set(prev).add(p.id));
        saveSound.mutate({ phraseId: p.id });
      } }] : []), ...promoteItem]
      : p.status === 'library'
        ? [
          ...promoteItem,
          { label: 'Remove from the library', disabled: removeSound.isPending, onClick: () => removeSound.mutate({ phraseId: p.id }) },
        ]
        : isOwner ? [{ label: 'Take back from global', disabled: shareSounds.isPending, onClick: () => shareSounds.mutate({ phraseIds: [p.id], global: false }) }] : [];
    const reclassify = PHRASE_KINDS.filter((k) => k !== p.kind).map((k) => ({
      label: `Reclassify as ${(KIND_BADGES[k]?.label ?? k).toLowerCase()}`,
      // A saved sound stays a sound and a saved voiceover a voiceover: either waits until it leaves the library.
      disabled: p.status !== 'described' && (SAVED_SOUND_KIND[k] === undefined || (k === 'voice') !== (p.kind === 'voice')),
      onClick: () => updatePhrase.mutate({ phraseId: p.id, kind: k }),
    }));
    return [...(library.length ? [...library, null] : []), ...reclassify];
  };
  // The footer counts whichever tab is open.
  const tabRows: { id: string; excluded: boolean }[] = mediaTab === 'audio' ? phraseRows : mediaTab === 'scenes' ? scenes : mediaTab === 'extras' ? extraRows : highlightRows;
  const tabKept = tabRows.filter((r) => !r.excluded).length;
  const ranges = useMemo(() => (mediaTab === 'audio'
    ? phraseRows.map((p) => ({ id: p.id, inMs: p.startMs, outMs: p.endMs, colour: p.colour, excluded: p.excluded }))
    : (mediaTab === 'scenes' ? scenes : mediaTab === 'extras' ? extraRows : highlightRows)
      .map((s) => ({ id: s.id, inMs: s.inMs, outMs: s.outMs, colour: s.colour, excluded: s.excluded }))), [highlightRows, extraRows, phraseRows, scenes, mediaTab]);

  const insideWord = useCallback((ms: number) => (d?.words ?? []).some((w) => ms > w.startMs && ms < w.endMs), [d?.words]);

  // Frame-tight range playback: play begins only after the seek lands, and a per-frame watcher stops
  // (or loops) at the out point instead of the coarse timeupdate tick that used to overrun.
  const playWatchRef = useRef<{ until: number; loopTo: number | null; raf: number } | null>(null);
  const stopWatch = () => {
    if (playWatchRef.current) {
      cancelAnimationFrame(playWatchRef.current.raf);
      playWatchRef.current = null;
    }
  };
  // A playRange whose seek never lands (seeking to the position the video is already at fires no
  // 'seeked') used to leave a stale once-listener armed; the NEXT seek from anywhere then started
  // playback with the old watcher. Every play/pause/scrub clears any pending begin first.
  const pendingBeginRef = useRef<(() => void) | null>(null);
  const clearPendingBegin = () => {
    const v = videoRef.current;
    if (v && pendingBeginRef.current) v.removeEventListener('seeked', pendingBeginRef.current);
    pendingBeginRef.current = null;
  };
  const playRange = (fromMs: number, untilMs: number, loop = false) => {
    const v = videoRef.current;
    if (!v || untilMs <= fromMs) return;
    stopWatch();
    clearPendingBegin();
    v.pause();
    pendingSeekRef.current = null;
    const begin = () => {
      pendingBeginRef.current = null;
      void v.play();
      const tick = () => {
        const st = playWatchRef.current;
        if (!st) return;
        if (v.currentTime * 1000 >= st.until - FRAME_WATCH_LEAD_MS) {
          if (st.loopTo !== null) v.currentTime = insideEdge(st.loopTo) / 1000;
          else {
            // The tick can land a frame late; the seek settles the picture on the last kept frame.
            v.pause();
            v.currentTime = (st.until - EDGE_INSET_MS) / 1000;
            stopWatch();
            return;
          }
        }
        st.raf = requestAnimationFrame(tick);
      };
      playWatchRef.current = { until: untilMs, loopTo: loop ? fromMs : null, raf: requestAnimationFrame(tick) };
    };
    if (Math.abs(v.currentTime * 1000 - insideEdge(fromMs)) < 1) {
      begin();
    } else {
      pendingBeginRef.current = begin;
      v.addEventListener('seeked', begin, { once: true });
      v.currentTime = insideEdge(fromMs) / 1000;
    }
  };
  const pausePlayback = () => {
    stopWatch();
    clearPendingBegin();
    videoRef.current?.pause();
  };
  const stopPlayback = (fromMs: number) => {
    stopWatch();
    clearPendingBegin();
    const v = videoRef.current;
    if (v) {
      v.pause();
      v.currentTime = insideEdge(fromMs) / 1000;
    }
  };
  const selectSnippet = (id: string) => {
    setSelected(id);
    setStaged(null);
    const s = d?.snippets.find((x) => x.id === id);
    const p = phraseRows.find((x) => x.id === id);
    if (s) playRange(s.inMs, s.outMs);
    else if (p) playRange(p.startMs, p.endMs);
  };
  const onVideoTime = (ms: number) => setPlayheadMs(ms);

  // Edits save on release, exactly like the composer; keyboard nudges settle after a short pause.
  // Overlapping another snippet is allowed on purpose; the multiply blend shows the overlap.
  const commitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resizeRef = useRef<{ startY: number; startH: number } | null>(null);
  const videoBoxRef = useRef<HTMLDivElement>(null);
  const pendingSeekRef = useRef<number | null>(null);
  const descTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [descDrafts, setDescDrafts] = useState<Record<string, string>>({});
  const [viewAspect, setViewAspect] = useState<'16:9' | '1:1' | '9:16' | null>(null);
  const { peek, showPeek } = useAspectPeek();
  const [recentOpen, setRecentOpen] = useState(false);
  const [tagging, setTagging] = useState(false);
  const attachVoice = useMutation(trpc.compose.attachVoice.mutationOptions());
  // Audio-line surgery: an open word-gap strip for one line, and a merge selection across lines.
  const [splitting, setSplitting] = useState<string | null>(null);
  const [mergeSel, setMergeSel] = useState<Set<string>>(new Set());
  // Remix video: the open tab's switched-on scenes or highlights become a fresh composition for the
  // platform chosen in RemixModal, cut to the brand's pace and priorities; the working draft is never
  // touched, so nothing in the Composer is lost.
  const [remixOpen, setRemixOpen] = useState(false);
  const remix = useMutation(trpc.compose.remix.mutationOptions({
    onSuccess: (data) => {
      setRemixOpen(false);
      // Fresh: the Composer treats its sounds as just arrived, so a song skips its silent opening and is levelled.
      if (confirmLeave()) void navigate({ to: '/compose', search: { draft: data.draftId, fresh: true } });
    },
  }));
  const splitPhrase = useMutation(trpc.assets.splitPhrase.mutationOptions({ onSuccess: async () => { setSplitting(null); await refresh(); } }));
  const mergePhrases = useMutation(trpc.assets.mergePhrases.mutationOptions({ onSuccess: async () => { setMergeSel(new Set()); await refresh(); } }));
  const toggleTick = (id: string, on: boolean) => setMergeSel((prev) => {
    const next = new Set(prev);
    if (on) next.add(id); else next.delete(id);
    return next;
  });
  // Every tab merges its ticked rows the same way: spoken lines through their own join, video rows through theirs.
  const merging = mergePhrases.isPending || mergeSnippets.isPending;
  const mergeTicked = () => {
    const ids = [...mergeSel];
    if (ids.length < 2) return;
    if (mediaTab === 'audio') mergePhrases.mutate({ phraseIds: ids });
    else mergeSnippets.mutate({ snippetIds: ids }, { onSuccess: (r) => { setMergeSel(new Set()); setSelected(r.id); setStaged(null); } });
  };
  const mergeItem = { label: `Merge ticked rows${mergeSel.size > 1 ? ` (${mergeSel.size})` : ''}`, disabled: mergeSel.size < 2 || merging, onClick: mergeTicked };
  // Add opens a new row on the whole clip, selected, so its handles sit at the two ends ready to drag.
  const addRow = (kind: 'highlight' | 'scene') => {
    if (!d) return;
    addSnippet.mutate({ assetId: d.id, kind, inMs: 0, outMs: durationMs }, { onSuccess: (r) => { setSelected(r.id); setStaged(null); } });
  };
  // Split leaves the left half selected, its handles ready to set.
  const splitRow = (id: string) => splitSnippet.mutate({ snippetId: id }, { onSuccess: (r) => { setSelected(r.leftId); setStaged(null); } });
  const openTab = (tab: typeof mediaTab) => {
    setMediaTab(tab);
    setSelected(null);
    setStaged(null);
    setMergeSel(new Set());
  };
  useEffect(() => {
    setDescDrafts({});
    setViewAspect(null);
    setDirty(false);
    setSplitting(null);
    setMergeSel(new Set());
    undoRef.current = [];
    redoRef.current = [];
    setHistTick((t) => t + 1);
  }, [assetId]);
  // A deep link pre-selects one row, a video row or an audio snippet, on its own tab ("Adjust start and
  // end points" from Browse and Tagging).
  const linkedRow = search.snippet ? detail.data?.snippets.find((s) => s.id === search.snippet) ?? null : null;
  const linkedPhrase = search.snippet && !linkedRow ? detail.data?.phrases.find((p) => p.id === search.snippet) ?? null : null;
  const deepLinkSnippet = linkedRow?.id ?? linkedPhrase?.id ?? null;
  const deepLinkTab: typeof mediaTab | null = linkedPhrase ? 'audio'
    : linkedRow ? (linkedRow.kind === 'highlight' ? 'highlights' : linkedRow.kind === 'extra' ? 'extras' : 'scenes') : null;
  useEffect(() => {
    if (!deepLinkSnippet || !deepLinkTab) return;
    setMediaTab(deepLinkTab);
    setSelected(deepLinkSnippet);
  }, [deepLinkSnippet, deepLinkTab]);

  // Undo and redo: snapshots of a snippet before each edit (trim, nudge, role, exclude, description),
  // up to ten deep, restored through the same save path everything else uses.
  type SnippetSnap = { snippetId: string; inMs: number; outMs: number; role: string | null; description: string | null; excluded: boolean; muted: boolean };
  const undoRef = useRef<SnippetSnap[]>([]);
  const redoRef = useRef<SnippetSnap[]>([]);
  const [, setHistTick] = useState(0);
  const snapOf = (id: string): SnippetSnap | null => {
    const s = detail.data?.snippets.find((x) => x.id === id);
    return s ? { snippetId: id, inMs: s.inMs, outMs: s.outMs, role: s.role, description: s.description, excluded: s.excluded, muted: s.muted } : null;
  };
  const recordHistory = (id: string) => {
    const s = snapOf(id);
    if (!s) return;
    undoRef.current = [...undoRef.current.slice(-9), s];
    redoRef.current = [];
    setHistTick((t) => t + 1);
  };
  const applySnap = (s: SnippetSnap) => {
    // Clear the staged drag so the zoom handles visibly snap back to the restored range.
    setStaged(null);
    update.mutate({ snippetId: s.snippetId, inMs: s.inMs, outMs: s.outMs, role: s.role, description: s.description, excluded: s.excluded, muted: s.muted });
  };
  const undoEdit = () => {
    const s = undoRef.current.pop();
    if (!s) return;
    const cur = snapOf(s.snippetId);
    if (cur) redoRef.current.push(cur);
    setDescDrafts((p) => ({ ...p, [s.snippetId]: s.description ?? '' }));
    applySnap(s);
    setHistTick((t) => t + 1);
  };
  const redoEdit = () => {
    const s = redoRef.current.pop();
    if (!s) return;
    const cur = snapOf(s.snippetId);
    if (cur) undoRef.current.push(cur);
    setDescDrafts((p) => ({ ...p, [s.snippetId]: s.description ?? '' }));
    applySnap(s);
    setHistTick((t) => t + 1);
  };
  // Edits persist instantly, but Save and index is the hand-off into tagging; leaving before it
  // deserves a warning rather than silent limbo.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  /** Scrub the preview to the dragged edge; queued so fast drags always settle on the last frame. */
  const scrubTo = (ms: number) => {
    const v = videoRef.current;
    if (!v) return;
    stopWatch();
    clearPendingBegin();
    if (!v.paused) v.pause();
    pendingSeekRef.current = ms;
    if (!v.seeking) v.currentTime = ms / 1000;
  };
  const onEdge = (which: 'in' | 'out', ms: number, done = false) => {
    if (!sel) return;
    setLastHandle(which);
    setStaged((prev) => {
      const cur = prev ?? { inMs: sel.inMs, outMs: sel.outMs };
      const next = which === 'in' ? { ...cur, inMs: Math.min(ms, cur.outMs - Math.round(frameMs)) } : { ...cur, outMs: Math.max(ms, cur.inMs + Math.round(frameMs)) };
      // The picture shows the first kept frame for In and the last kept frame for Out, never the neighbour.
      scrubTo(which === 'in' ? insideEdge(next.inMs) : next.outMs - EDGE_INSET_MS);
      if (done) {
        if (sel.kind === 'snippet') {
          recordHistory(sel.id);
          update.mutate({ snippetId: sel.id, inMs: Math.round(next.inMs), outMs: Math.round(next.outMs) });
        } else if (sel.kind === 'phrase') {
          updatePhrase.mutate({ phraseId: sel.id, startMs: Math.round(next.inMs), endMs: Math.round(next.outMs) });
        }
      }
      return next;
    });
  };

  /** One frame on the last-touched handle; shared by the arrow keys and the transport buttons. */
  const nudgeFrame = (dir: -1 | 1) => {
    if (!sel) return;
    const target = Math.round((lastHandle === 'in' ? inMs : outMs) + dir * frameMs);
    onEdge(lastHandle, target, false);
    if (commitTimer.current) clearTimeout(commitTimer.current);
    commitTimer.current = setTimeout(() => onEdge(lastHandle, target, true), 500);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === ' ') {
        const v = videoRef.current;
        if (!v?.src) return;
        e.preventDefault();
        // Deterministic: with a selection, space replays exactly that selection; the old unconstrained
        // play-on left the transport in an uncertain state.
        if (!v.paused) pausePlayback();
        else if (sel) playRange(inMs, outMs);
        else {
          stopWatch();
          clearPendingBegin();
          void v.play();
        }
        return;
      }
      if (!sel || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
      e.preventDefault();
      nudgeFrame(e.key === 'ArrowLeft' ? -1 : 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const zoomPad = Math.max(1000, Math.round((outMs - inMs) * 0.2));
  const zoomStart = Math.max(0, inMs - zoomPad);
  const zoomEnd = Math.min(durationMs, outMs + zoomPad);
  const analysing = d?.status === 'awaiting_upload';
  // A project's joins: where each file after the first begins on the timeline.
  const joins = useMemo(() => (d?.parts ?? []).slice(1).filter((p) => p.offsetMs !== null).map((p) => ({ atMs: p.offsetMs!, name: p.name })), [d?.parts]);
  // A fresh analysis, or a freshly opened clip, opens on Video scenes.
  const wasAnalysing = useRef(false);
  useEffect(() => {
    if (wasAnalysing.current && !analysing) {
      setMediaTab('scenes');
      setSelected(null);
      setStaged(null);
    }
    wasAnalysing.current = analysing;
  }, [analysing]);
  // A link naming a row opens on that row's tab instead (above), even when the clip is already loaded.
  useEffect(() => { if (!deepLinkTab) setMediaTab('scenes'); }, [assetId]);
  const closeClip = () => {
    if (confirmLeave()) {
      setAssetId(null);
      setDirty(false);
      setSelected(null);
      setStaged(null);
    }
  };
  const loadNext = () => {
    const rows = (list.data ?? []).filter((a) => a.id !== assetId && (a.status === 'awaiting_snippet' || a.status === 'confirming'));
    if (rows[0]) openAsset(rows[0].id);
    else if (confirmLeave()) void navigate({ to: '/library' });
  };
  // The default view is the clip's own shape, so the viewer opens on the whole frame, cropping nothing.
  // Choosing another shape crops to its centre; the aspect buttons pop the uncropped mockup beside them.
  const aspect = viewAspect ?? clipAspect(d?.width ?? null, d?.height ?? null);
  // Scenes remix from the Video scenes tab; every other tab remixes the highlights.
  const remixFrom = mediaTab === 'scenes' ? 'scene' as const : 'highlight' as const;
  const remixable = (remixFrom === 'scene' ? scenes : highlightRows).some((r) => !r.excluded);
  const remixClip = () => { remix.reset(); setRemixOpen(true); };

  return (
    <>
      <TopBar title="Analyse">
        {d && <span className="sub crumb">{d.name}</span>}
      </TopBar>
      <section className="content upload-content">
        {d && (
          <div className="toolstrip page-toolstrip">
            {analysing
              ? (
                <div className="row analyse-master-progress">
                  <AnalyseProgress stage={d.progressStage} progress={d.analyseProgress} etaAt={d.analyseEtaAt} big />
                  <button type="button" className="btn quiet" title="Close this clip; the analysis carries on" onClick={closeClip}>Close</button>
                </div>
              )
              : <AnalysePanel assetId={d.id} durationMs={durationMs} stored={d.analyseParams} segmented={d.snippets.length > 0}
                history={{ undo: undoEdit, redo: redoEdit, canUndo: undoRef.current.length > 0, canRedo: redoRef.current.length > 0 }}
                onRecent={() => setRecentOpen(true)}
                onQueued={refresh}
                actions={{
                  upload: () => { if (confirmLeave()) void navigate({ to: '/upload' }); },
                  load: () => { if (confirmLeave()) void navigate({ to: '/library' }); },
                  next: loadNext,
                  close: closeClip,
                  composer: remixClip,
                  composerDisabled: !remixable || remix.isPending,
                  save: d.status !== 'indexed' ? () => confirm.mutate({ assetIds: [d.id] }) : null,
                  savePending: confirm.isPending,
                  tag: () => { if (confirmLeave()) void navigate({ to: '/tagging', search: { asset: d.id } }); },
                }} />}
          </div>
        )}
        {saveResult && (
          <div className="card pad save-note" role="status">
            {saveResult.indexed > 0 && <span className="chip ok">{saveResult.indexed} indexed, now in Awaiting tagging</span>}
            {saveResult.skipped.map((s) => <span key={s.name} className="chip warn">{s.name} skipped: {s.reason}</span>)}
            {saveResult.indexed === 0 && saveResult.skipped.length === 0 && <span className="chip">Nothing needed saving.</span>}
            <button type="button" className="btn quiet" onClick={() => setSaveResult(null)}>Dismiss</button>
          </div>
        )}
        {confirm.isError && <div className="card pad error" role="alert">Save failed: {confirm.error.message}</div>}
        {!assetId ? (
          <div className="empty-start">
            Nothing loaded.
            <div className="row tagq-empty-row">
              <Link to="/upload" className="btn lg btn-iconed"><Icon name="upload" /> Upload new clips</Link>
              <button type="button" className="btn primary lg btn-iconed" onClick={() => void navigate({ to: '/browse', search: { pick: 'analyse' } })}><Icon name="folder" /> Browse</button>
            </div>
            <RecentFiles onOpen={openAsset} />
          </div>
        ) : detail.isError ? (
          <div className="card pad error" role="alert">{detail.error.message}</div>
        ) : !d ? (
          <div className="empty"><div className="spinner" aria-label="Loading" /></div>
        ) : analysing && d.snippets.length === 0 ? (
          <div className="card pad analysing-card" role="status">
            <div className="h2">{d.name}</div>
            <span className="sub">This screen updates itself when it lands.</span>
            <AnalyseProgress stage={d.progressStage} progress={d.analyseProgress} etaAt={d.analyseEtaAt} big />
          </div>
        ) : (
          <>
            <div className={`card zoom-card${analysing ? ' busy' : ''}`}>
              <div className="zoom-head">
                {sel ? (
                  <>
                    <MiniSwatch colour={sel.colour} />
                    <span className="chip acc">Adjusting · {sel.label}</span>
                    <span className="sub mono zoom-times">{fmtTime(inMs)} – {fmtTime(outMs)} · {((outMs - inMs) / 1000).toFixed(1)} s</span>
                  </>
                ) : (
                  <span className="sub">Click a snippet to adjust it</span>
                )}
                <div className="aspect-peek-holder aspect-seg-holder">
                  <div className="seg aspect-seg">
                    {(['16:9', '1:1', '9:16'] as const).map((a) => (
                      <button key={a} type="button" className={aspect === a ? 'on' : ''}
                        onClick={() => { setViewAspect(a); showPeek(a); }}>{a}</button>
                    ))}
                  </div>
                  <AspectPeek format={peek} posterUrl={d.posterUrl} />
                </div>
              </div>
              <div ref={videoBoxRef} className={`zoom-video-box ar-${aspect.replace(':', '-')}`}>
                <video ref={videoRef} className="zoom-video" src={d.proxyUrl ?? undefined} poster={d.posterUrl ?? undefined} controls playsInline preload="metadata"
                  onTimeUpdate={(e) => onVideoTime(Math.round(e.currentTarget.currentTime * 1000))}
                  onSeeked={(e) => {
                    const want = pendingSeekRef.current;
                    if (want !== null && Math.abs(e.currentTarget.currentTime * 1000 - want) > 40) e.currentTarget.currentTime = want / 1000;
                  }} />
              </div>
              <div className="viewport-resizer" role="separator" aria-label="Drag to resize the preview"
                onPointerDown={(e) => {
                  e.currentTarget.setPointerCapture(e.pointerId);
                  resizeRef.current = { startY: e.clientY, startH: videoBoxRef.current?.clientHeight ?? 240 };
                }}
                onPointerMove={(e) => {
                  const r = resizeRef.current;
                  if (!r || !videoBoxRef.current) return;
                  const h = Math.max(140, Math.min(560, r.startH + (e.clientY - r.startY)));
                  videoBoxRef.current.style.setProperty('--viewport-h', `${Math.round(h)}px`);
                }}
                onPointerUp={() => { resizeRef.current = null; }}>
                <span className="viewport-grip" aria-hidden="true" />
              </div>
              <div className="zoom-body">
                <div className="row wf-legend-row">
                  <div className="label">Sound · whole clip</div>
                  <div className="wf-legend"><span><i className="lg-speech" />speech</span><span><i className="lg-noise" />kitchen noise</span><span><i className="lg-quiet" />quiet</span></div>
                </div>
                <div className="wf-holder">
                  <Waveform peaks={d.peaks} durationMs={durationMs} regions={d.regions} words={d.words} sceneCuts={d.sceneCuts} joins={joins}
                    ranges={ranges} selectedId={selected} staged={staged} playheadMs={playheadMs} height={84} showWords
                    onSelect={selectSnippet} onEdge={onEdge} onSeek={(ms) => { if (videoRef.current) videoRef.current.currentTime = ms / 1000; }} />
                </div>
                <BlockStrip ranges={ranges} durationMs={durationMs} selectedId={selected} onSelect={selectSnippet} />
                <div className="wf-axis mono">
                  {[0, 0.25, 0.5, 0.75, 1].map((f) => <span key={f}>{fmtTime(Math.round(durationMs * f), false)}</span>)}
                </div>
                {sel && (
                  <>
                    <div className="wf-axis mono zoom-axis">
                      {[0, 0.25, 0.5, 0.75, 1].map((f) => <span key={f}>{fmtTime(Math.round(zoomStart + (zoomEnd - zoomStart) * f))}</span>)}
                    </div>
                    <AccentBar colour={sel.colour} />
                    <div className="zoom-label" aria-hidden="true">
                      <MiniSwatch colour={sel.colour} />
                      <div className="label">{sel.label}</div>
                      <span className="zoom-label-words">
                        {d.words.filter((w) => w.endMs > inMs && w.startMs < outMs).map((w) => w.word).join(' ')}
                      </span>
                      <span className="chip mono time-pill">{fmtTime(inMs)} – {fmtTime(outMs)}</span>
                    </div>
                    <div className="wf-holder zoomed">
                      <Waveform peaks={d.peaks} durationMs={durationMs} windowStartMs={zoomStart} windowEndMs={zoomEnd}
                        regions={d.regions} words={d.words} sceneCuts={d.sceneCuts} joins={joins} ranges={ranges} selectedId={selected}
                        staged={staged} playheadMs={playheadMs} height={84} showWords onEdge={onEdge} />
                    </div>
                    <div className="row zoom-transport">
                      <Toolbar dense>
                        <Tool icon="play" title="Play this selection once" onClick={() => playRange(inMs, outMs, false)} />
                        <Tool icon="loop" title="Play this selection on loop" onClick={() => playRange(inMs, outMs, true)} />
                        <Tool icon="pause" title="Pause" onClick={pausePlayback} />
                        <Tool icon="stop" title="Stop and return to the selection start" onClick={() => stopPlayback(inMs)} />
                        <Tool icon="chev-l" title="Click on a drag handle and adjust one frame" onClick={() => nudgeFrame(-1)} />
                        <Tool icon="chev-r" title="Click on a drag handle and adjust one frame" onClick={() => nudgeFrame(1)} />
                        <Tool icon="eye" title="Show or hide the In and Out details" active={detailsOpen} onClick={() => setDetailsOpen(!detailsOpen)} />
                      </Toolbar>
                    </div>
                    {detailsOpen && (
                      <div className="row inout-row">
                        <EdgeReadout k="In" ms={inMs} inWord={insideWord(inMs)} />
                        <EdgeReadout k="Out" ms={outMs} inWord={insideWord(outMs)} />
                      </div>
                    )}
                  </>
                )}
              </div>
              {detailsOpen && (
                <div className="zoom-foot">
                  <div className="sub mono zoom-foot-meta">{fmtBytes(d.bytes)} · {d.width}×{d.height} · {Math.round(durationMs / 1000)} s</div>
                </div>
              )}
            </div>
            <div className={`snips-col${analysing ? ' busy' : ''}`}>
              <div className="row snips-head">
                <div className="seg upload-tabs">
                  <button type="button" className={mediaTab === 'scenes' ? 'on' : ''} onClick={() => openTab('scenes')}>Video scenes</button>
                  <button type="button" className={mediaTab === 'audio' ? 'on' : ''} onClick={() => openTab('audio')}>Audio snippets</button>
                  <button type="button" className={mediaTab === 'highlights' ? 'on' : ''} onClick={() => openTab('highlights')}>Video highlights</button>
                  <button type="button" className={mediaTab === 'extras' ? 'on' : ''} title="Frame-tight stand-outs: steady colour signatures and still pack shots, however short"
                    onClick={() => openTab('extras')}>Video extras{extraRows.length ? ` · ${extraRows.length}` : ''}</button>
                </div>
                <div className="snips-actions">
                  {mediaTab === 'audio' && (soundKinds.length > 1 || soundStatuses.length > 0) && (
                    <div className="sound-legend" aria-label="Sound kinds">
                      {soundKinds.map((k) => <span key={k} className={`snd-${k}`}><i />{SOUND_LEGEND[k] ?? k}</span>)}
                      {soundStatuses.map((st) => <span key={st}><SoundStatusIcon status={st} />{SOUND_STATUS_LABELS[st]}</span>)}
                    </div>
                  )}
                  {mergeSel.size > 0 && (
                    <>
                      <button type="button" className="btn quiet" disabled={tabRows.every((r) => r.excluded || mergeSel.has(r.id))}
                        onClick={() => setMergeSel(new Set(tabRows.filter((r) => !r.excluded).map((r) => r.id)))}>All</button>
                      <button type="button" className="btn quiet" onClick={() => setMergeSel(new Set())}>None</button>
                    </>
                  )}
                  {mergeSel.size > 1 && (
                    <button type="button" className="btn" disabled={merging} onClick={mergeTicked}>
                      {merging ? 'Merging…' : `Merge ${mergeSel.size}`}
                    </button>
                  )}
                  {(mediaTab === 'highlights' || mediaTab === 'scenes') && (
                    <button type="button" className="btn primary lg btn-iconed" disabled={!remixable || remix.isPending}
                      title={`A new edit for a platform from the switched-on ${remixFrom}s; the current composition stays as it is`}
                      onClick={remixClip}>
                      <Icon name="compose" /> {remix.isPending ? 'Remixing…' : 'Remix video'}
                    </button>
                  )}
                </div>
              </div>
              {tagging && <TagAssetsModal assetIds={[d.id]} onClose={() => setTagging(false)} />}
              {recentOpen && (
                <RecentModal currentId={assetId} onOpen={openAsset} onClose={() => setRecentOpen(false)}
                  onLibrary={() => { if (confirmLeave()) void navigate({ to: '/library' }); }} />
              )}
              {mediaTab === 'audio' ? (
                <div className="snips-list">
                  {phraseRows.map((p) => {
                    const phraseWords = d.words.filter((w) => (w.startMs + w.endMs) / 2 >= p.startMs && (w.startMs + w.endMs) / 2 <= p.endMs);
                    const heading = p.title ?? KIND_BADGES[p.kind]?.label ?? p.kind;
                    return (
                      <Fragment key={p.id}>
                        {p.excluded ? (
                          // Switched off: out of the index, a slim grey row; the swatch brings it back.
                          <div className={`snip off${p.id === selected ? ' sel-row' : ''}`}>
                            <MergeTick id={p.id} ticked={mergeSel.has(p.id)} onTick={toggleTick} />
                            {phraseSwitching === p.id ? <span className="spinner swatch-spinner" aria-label="Switching on" /> : <MiniSwatch colour={p.colour} off onToggle={() => updatePhrase.mutate({ phraseId: p.id, excluded: false })} />}
                            <button type="button" className="mini-btn compact" onClick={() => selectSnippet(p.id)} aria-label="Zoom this line">
                              <MiniWave peaks={d.peaks} durationMs={durationMs} inMs={p.startMs} outMs={p.endMs} colour={OFF_ROW_COLOUR} regions={d.regions} />
                            </button>
                            <span className="snip-off-text"><b>{heading}</b><span className="sub"> · {p.text}</span></span>
                            <span className="snip-conf">
                              <span className="chip mono time-pill">{fmtTime(p.startMs, false)} – {fmtTime(p.endMs, false)}</span>
                              <RowMenu items={[
                                { label: 'Switch on', onClick: () => updatePhrase.mutate({ phraseId: p.id, excluded: false }) },
                                { label: 'Play this line', onClick: () => playRange(p.startMs, p.endMs) },
                                mergeItem,
                              ]} />
                            </span>
                          </div>
                        ) : (
                          <div className={`snip snd-${p.kind}${p.status === 'global' ? ' is-global' : ''}${p.id === selected ? ' sel-row' : ''}`}>
                            <MergeTick id={p.id} ticked={mergeSel.has(p.id)} onTick={toggleTick} />
                            {phraseSwitching === p.id ? <span className="spinner swatch-spinner" aria-label="Switching off" /> : <MiniSwatch colour={p.colour} onToggle={() => updatePhrase.mutate({ phraseId: p.id, excluded: true })} />}
                            <button type="button" className="mini-btn" onClick={() => selectSnippet(p.id)} aria-label="Zoom this spoken line">
                              <MiniWave peaks={d.peaks} durationMs={durationMs} inMs={p.startMs} outMs={p.endMs} colour={p.colour} regions={d.regions} />
                            </button>
                            <button type="button" className="cut-btn" title="Split this audio further" aria-label="Split this audio further"
                              disabled={phraseWords.length < 2}
                              onClick={() => setSplitting(splitting === p.id ? null : p.id)}>
                              <Icon name="scissors" />
                            </button>
                            <span className="scene-text">
                              <input key={`h${p.id}:${p.title ?? ''}`} className="heading-edit" defaultValue={p.title ?? ''} placeholder="Heading" maxLength={HEADING_MAX_CHARS} aria-label="Line heading"
                                onKeyDown={commitOnEnter}
                                onBlur={(e) => { const v = e.target.value.trim(); if (v !== (p.title ?? '')) updatePhrase.mutate({ phraseId: p.id, title: v || null }); }} />
                              {p.kind === 'voice'
                                ? <span className="snip-q">&quot;{p.text}&quot;</span>
                                : (
                                  // A described sound's words are the AI's reading of it; click to retype them, Enter saves.
                                  <textarea key={`t${p.id}:${p.text}`} className="snip-q-edit" rows={1} defaultValue={p.text} maxLength={DESCRIPTION_MAX_CHARS}
                                    aria-label="Sound description" onKeyDown={commitOnEnter}
                                    onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== p.text) updatePhrase.mutate({ phraseId: p.id, text: v }); }} />
                                )}
                            </span>
                            <span className="snip-conf">
                              {savingSounds.has(p.id) && p.status === 'described' && <span className="spinner swatch-spinner" aria-label="Saving to the library" />}
                              {/* Its own column, left of the kind icons, so those line up down the list. */}
                              <span className={`sound-status ${p.status}`}
                                title={p.status === 'global'
                                  ? (p.sharedBy === 'ai' ? 'Global, in every tenant\'s library: the listen found it cleanly isolated' : 'Global, in every tenant\'s library')
                                  : p.status === 'library' ? 'Saved to the brand\'s audio library' : undefined}>
                                {p.status !== 'described' && <SoundStatusIcon status={p.status} />}
                              </span>
                              <span className="kind-badge" title={KIND_BADGES[p.kind]?.label ?? p.kind}>
                                <Icon name={KIND_BADGES[p.kind]?.icon ?? 'wave'} />
                              </span>
                              <span className="chip mono time-pill">{fmtTime(p.startMs, false)} – {fmtTime(p.endMs, false)}</span>
                              {starsOf('phrase', p.id)}
                              <RowMenu items={[
                                ...rateItems('phrase', p.id),
                                { label: 'Play this line', onClick: () => playRange(p.startMs, p.endMs) },
                                { label: 'Loop this line', onClick: () => playRange(p.startMs, p.endMs, true) },
                                { label: 'Adjust start and end points', onClick: () => selectSnippet(p.id) },
                                null,
                                { label: splitting === p.id ? 'Close the split view' : 'Split this line further', disabled: phraseWords.length < 2, onClick: () => setSplitting(splitting === p.id ? null : p.id) },
                                mergeItem,
                                { label: 'Switch off', onClick: () => updatePhrase.mutate({ phraseId: p.id, excluded: true }) },
                                null,
                                ...soundMenu(p),
                                null,
                                { label: 'Use as the Composer voiceover', onClick: () => attachVoice.mutate({ assetId: d.id, startMs: p.startMs, endMs: p.endMs }) },
                                { label: 'Tag this clip…', onClick: () => setTagging(true) },
                                { label: 'Open in tagging', onClick: () => openInTagging({ phrase: p.id }) },
                              ]} />
                            </span>
                          </div>
                        )}
                        {splitting === p.id && !p.excluded && (
                          <div className="split-strip" role="group" aria-label="Choose where to split this line">
                            <div className="split-words">
                              {phraseWords.map((w, i) => (
                                <Fragment key={w.idx}>
                                  <span className="split-word">{w.word}</span>
                                  {i < phraseWords.length - 1 && (
                                    <button type="button" className="split-gap" disabled={splitPhrase.isPending}
                                      title="Split here" aria-label={`Split between ${w.word} and ${phraseWords[i + 1]!.word}`}
                                      onClick={() => splitPhrase.mutate({ phraseId: p.id, atMs: Math.round((w.endMs + phraseWords[i + 1]!.startMs) / 2) })}>
                                      <Icon name="scissors" />
                                    </button>
                                  )}
                                </Fragment>
                              ))}
                            </div>
                            <span className="sub split-hint">Hover the words; a cut mark appears at each gap.</span>
                          </div>
                        )}
                      </Fragment>
                    );
                  })}
                  {d.phrases.length === 0 && <div className="empty">No spoken lines in this clip; audio snippets come from its transcript.</div>}
                  {attachVoice.isSuccess && <span className="chip ok">Set as the Composer&apos;s voiceover layer</span>}
                  {attachVoice.isError && <div className="error" role="alert">{attachVoice.error.message}</div>}
                  {splitPhrase.isError && <div className="error" role="alert">{splitPhrase.error.message}</div>}
                  {mergePhrases.isError && <div className="error" role="alert">{mergePhrases.error.message}</div>}
                </div>
              ) : mediaTab === 'scenes' ? (
                <div className="snips-list">
                  {scenes.map((sc) => sc.excluded ? (
                    // Switched off: out of the index, a slim grey row; the swatch brings it back.
                    <div key={sc.id} className={`snip off${sc.id === selected ? ' sel-row' : ''}`}>
                      <MergeTick id={sc.id} ticked={mergeSel.has(sc.id)} onTick={toggleTick} />
                      {switching === sc.id ? <span className="spinner swatch-spinner" aria-label="Switching on" /> : <MiniSwatch colour={sc.colour} off onToggle={() => { recordHistory(sc.id); update.mutate({ snippetId: sc.id, excluded: false }); }} />}
                      <button type="button" className="mini-btn compact" onClick={() => selectSnippet(sc.id)} aria-label={`Play scene ${sc.index}`}>
                        <MiniWave peaks={d.peaks} durationMs={durationMs} inMs={sc.inMs} outMs={sc.outMs} colour={OFF_ROW_COLOUR} regions={d.regions} />
                      </button>
                      <span className="snip-dur mono">{((sc.outMs - sc.inMs) / 1000).toFixed(1)} s</span>
                      <span className="snip-off-text"><b>Scene {sc.index}</b>{sc.title || sc.description ? <span className="sub"> · {sc.title ?? sc.description}</span> : null}</span>
                      <span className="snip-conf">
                        <span className="chip mono time-pill">{fmtTime(sc.inMs, false)} – {fmtTime(sc.outMs, false)}</span>
                        <RowMenu items={[
                          { label: 'Switch on', onClick: () => { recordHistory(sc.id); update.mutate({ snippetId: sc.id, excluded: false }); } },
                          { label: 'Play this scene', onClick: () => selectSnippet(sc.id) },
                          mergeItem,
                        ]} />
                      </span>
                    </div>
                  ) : (
                    <div key={sc.id} className={`snip${sc.id === selected ? ' sel-row' : ''}`}>
                      <MergeTick id={sc.id} ticked={mergeSel.has(sc.id)} onTick={toggleTick} />
                      {switching === sc.id ? <span className="spinner swatch-spinner" aria-label="Switching off" /> : <MiniSwatch colour={sc.colour} onToggle={() => { recordHistory(sc.id); update.mutate({ snippetId: sc.id, excluded: true }); }} />}
                      <button type="button" className="mini-btn" onClick={() => selectSnippet(sc.id)} aria-label={`Play scene ${sc.index}`}>
                        <MiniWave peaks={d.peaks} durationMs={durationMs} inMs={sc.inMs} outMs={sc.outMs} colour={sc.colour} regions={d.regions} />
                      </button>
                      <button type="button" className="cut-btn" title="Split this scene in two" aria-label="Split this scene in two"
                        disabled={splitSnippet.isPending} onClick={() => splitRow(sc.id)}>
                        <Icon name="scissors" />
                      </button>
                      <span className="scene-label">Scene {sc.index}</span>
                      <span className="snip-dur mono">{((sc.outMs - sc.inMs) / 1000).toFixed(1)} s</span>
                      <span className="scene-text">
                        <input key={`t${sc.id}:${sc.title ?? ''}`} className="heading-edit" defaultValue={sc.title ?? ''} placeholder="Name this scene" maxLength={HEADING_MAX_CHARS}
                          aria-label="Scene heading" onKeyDown={commitOnEnter}
                          onBlur={(e) => { const v = e.target.value.trim(); if (v !== (sc.title ?? '')) update.mutate({ snippetId: sc.id, title: v || null }); }} />
                        <textarea key={`d${sc.id}:${sc.description ?? ''}`} className="snip-q-edit" rows={2} defaultValue={sc.description ?? ''} placeholder="Describe this scene…" maxLength={DESCRIPTION_MAX_CHARS}
                          aria-label="Scene description" onKeyDown={commitOnEnter}
                          onBlur={(e) => { const v = e.target.value.trim(); if (v !== (sc.description ?? '')) update.mutate({ snippetId: sc.id, description: v || null }); }} />
                      </span>
                      <span className="snip-conf">
                        {sc.id === selected && staged && <span className="chip acc">adjusting</span>}
                        {composer.inDraft(sc.id) && <SentBadge />}
                        <span className="scene-flags">
                          {sc.scene?.toCamera && <span className="chip" title="Someone speaks or presents straight to camera">to camera</span>}
                          {sc.scene?.colourStable && <span className="chip" title="One dominant colour holds from the first frame to the last">steady colour</span>}
                          {sc.scene && sc.scene.sceneKind !== 'other' && <span className={`kind-badge ${sc.scene.sceneKind}`} title={SCENE_KIND_TIPS[sc.scene.sceneKind]} aria-label={SCENE_KIND_TIPS[sc.scene.sceneKind] ?? sc.scene.sceneKind}>{sc.scene.sceneKind === 'passage' ? '¶' : sc.scene.sceneKind === 'primary' ? 'H' : 'S'}</span>}
                        </span>
                        <span className="chip mono time-pill time-pill-even">{fmtTime(sc.inMs, false)} – {fmtTime(sc.outMs, false)}</span>
                        <span className="scene-swatches" title={sc.colourNames.join(', ') || 'Dominant colours'}>
                          {(sc.scene?.hexes ?? []).slice(0, 3).map((h) => <MiniSwatch key={h} colour={h} />)}
                        </span>
                        {starsOf('snippet', sc.id)}
                        <RowMenu items={[
                          ...rateItems('snippet', sc.id),
                          ...composer.snippetMenuItems(sc.id),
                          null,
                          { label: 'Play this scene', onClick: () => selectSnippet(sc.id) },
                          { label: 'Adjust start and end points', onClick: () => selectSnippet(sc.id) },
                          null,
                          { label: 'Split in two', disabled: splitSnippet.isPending, onClick: () => splitRow(sc.id) },
                          mergeItem,
                          { label: 'Keep as a highlight', disabled: addSnippet.isPending, onClick: () => {
                            addSnippet.mutate({ assetId: d.id, kind: 'highlight', inMs: sc.inMs, outMs: sc.outMs, role: sc.role, title: sc.title, description: sc.description });
                          } },
                          { label: 'Switch off', onClick: () => { recordHistory(sc.id); update.mutate({ snippetId: sc.id, excluded: true }); } },
                          null,
                          { label: 'Tag this clip…', onClick: () => setTagging(true) },
                          { label: 'Open in tagging', onClick: () => openInTagging({ snippet: sc.id }) },
                        ]} />
                      </span>
                    </div>
                  ))}
                  {scenes.length === 1 && <div className="sub snips-foot">One continuous scene: no visual cuts were detected in this clip.</div>}
                  <button type="button" className="btn snips-add" disabled={addSnippet.isPending} onClick={() => addRow('scene')}><Icon name="plus" /> Add scene</button>
                </div>
              ) : mediaTab === 'extras' ? (
                <div className="snips-list">
                  {extraRows.map((s) => (
                    <div key={s.id} className={`snip${s.excluded ? ' off' : ''}${s.id === selected ? ' sel-row' : ''}`}>
                      {analysing && <span className="spinner snip-spinner" aria-label="Re-analysing" />}
                      <MergeTick id={s.id} ticked={mergeSel.has(s.id)} onTick={toggleTick} />
                      {switching === s.id ? <span className="spinner swatch-spinner" aria-label="Switching" /> : <MiniSwatch colour={s.colour} off={s.excluded} onToggle={() => { recordHistory(s.id); update.mutate({ snippetId: s.id, excluded: !s.excluded }); }} />}
                      <button type="button" className={`mini-btn${s.excluded ? ' compact' : ''}`} onClick={() => selectSnippet(s.id)} aria-label={`Zoom ${s.role ?? 'this'} extra`}>
                        <MiniWave peaks={d.peaks} durationMs={durationMs} inMs={s.inMs} outMs={s.outMs} colour={s.excluded ? OFF_ROW_COLOUR : s.colour} regions={d.regions} />
                      </button>
                      <span className="snip-dur mono" title={`${Math.max(1, Math.round((s.outMs - s.inMs) / frameMs))} frames`}>{((s.outMs - s.inMs) / 1000).toFixed(2)} s</span>
                      <span className="snip-off-text"><b>{s.title ?? s.role ?? rowKindLabel(s.kind)}</b>{s.description ? <span className="sub"> · {s.description}</span> : null}</span>
                      <span className="snip-conf">
                        {s.role && <span className="chip" title={s.role === 'pack shot' ? 'The stillest run of a shot where the product or packaging fills the frame' : 'One dominant colour holds still from the first frame to the last'}>{s.role}</span>}
                        {composer.inDraft(s.id) && <SentBadge />}
                        <span className="chip mono time-pill">{fmtTime(s.inMs)} – {fmtTime(s.outMs)}</span>
                        {!s.excluded && starsOf('snippet', s.id)}
                        <RowMenu items={[
                          ...(!s.excluded ? [...rateItems('snippet', s.id), ...composer.snippetMenuItems(s.id)] : []),
                          null,
                          { label: 'Play this extra', onClick: () => selectSnippet(s.id) },
                          { label: 'Adjust start and end points', onClick: () => selectSnippet(s.id) },
                          null,
                          mergeItem,
                          { label: s.excluded ? 'Switch on' : 'Switch off', onClick: () => { recordHistory(s.id); update.mutate({ snippetId: s.id, excluded: !s.excluded }); } },
                          null,
                          { label: 'Tag this clip…', onClick: () => setTagging(true) },
                          { label: 'Open in tagging', onClick: () => openInTagging({ snippet: s.id }) },
                        ]} />
                      </span>
                    </div>
                  ))}
                  {extraRows.length === 0 && <div className="empty">No stand-out extras in this clip: nothing held one steady colour and no pack shot was found.</div>}
                  <div className="sub snips-foot">Extras are frame-tight and can be a few frames long: a steady colour signature, or the stillest run of a pack shot. Every one is its own snippet for search and the Composer.</div>
                </div>
              ) : (
              <div className="snips-list">
                {highlightRows.map((s) => s.excluded ? (
                  // Switched off: a slim row, the swatch its on switch, so the kept highlights carry the page.
                  <div key={s.id} className={`snip off${s.id === selected ? ' sel-row' : ''}`}>
                    {analysing && <span className="spinner snip-spinner" aria-label="Re-analysing" />}
                    <MergeTick id={s.id} ticked={mergeSel.has(s.id)} onTick={toggleTick} />
                    {switching === s.id ? <span className="spinner swatch-spinner" aria-label="Switching on" /> : <MiniSwatch colour={s.colour} off onToggle={() => { recordHistory(s.id); update.mutate({ snippetId: s.id, excluded: false }); }} />}
                    <button type="button" className="mini-btn compact" onClick={() => selectSnippet(s.id)} aria-label={`Zoom ${s.role ?? 'this'} snippet`}>
                      <MiniWave peaks={d.peaks} durationMs={durationMs} inMs={s.inMs} outMs={s.outMs} colour={OFF_ROW_COLOUR} regions={d.regions} />
                    </button>
                    <span className="snip-dur mono">{((s.outMs - s.inMs) / 1000).toFixed(1)} s</span>
                    <span className="snip-off-text"><b>{s.role ?? s.title ?? rowKindLabel(s.kind)}</b>{s.description ? <span className="sub"> · {s.description}</span> : null}</span>
                    <span className="snip-conf">
                      <span className="chip mono time-pill">{fmtTime(s.inMs, false)} – {fmtTime(s.outMs, false)}</span>
                      <RowMenu items={[
                        { label: 'Switch on', onClick: () => { recordHistory(s.id); update.mutate({ snippetId: s.id, excluded: false }); } },
                        { label: 'Play this snippet', onClick: () => selectSnippet(s.id) },
                        mergeItem,
                        null,
                        { label: 'Tag this clip…', onClick: () => setTagging(true) },
                      ]} />
                    </span>
                  </div>
                ) : (
                  <div key={s.id} className={`snip${s.id === selected ? ' sel-row' : ''}`}>
                    {analysing && <span className="spinner snip-spinner" aria-label="Re-analysing" />}
                    <MergeTick id={s.id} ticked={mergeSel.has(s.id)} onTick={toggleTick} />
                    {switching === s.id ? <span className="spinner swatch-spinner" aria-label="Switching off" /> : <MiniSwatch colour={s.colour} onToggle={() => { recordHistory(s.id); update.mutate({ snippetId: s.id, excluded: true }); }} />}
                    <button type="button" className="mini-btn" onClick={() => selectSnippet(s.id)} aria-label={`Zoom ${s.role ?? 'this'} snippet`}>
                      <MiniWave peaks={d.peaks} durationMs={durationMs} inMs={s.inMs} outMs={s.outMs} colour={s.colour} regions={d.regions} />
                    </button>
                    <span className="snip-dur mono">{((s.outMs - s.inMs) / 1000).toFixed(1)} s</span>
                    <span className="scene-text">
                    <span className="snip-heading">
                      <ManagedSelect value={s.role} placeholder="Classifier" ariaLabel="Snippet classifier" disabled={s.excluded}
                        options={roles.data ?? []}
                        onSelect={(label) => { recordHistory(s.id); update.mutate({ snippetId: s.id, role: label }); }}
                        onAdd={(label) => addRole.mutate({ label })}
                        onRename={(id, label) => renameRole.mutate({ roleId: id, label })}
                        onRemove={(id) => removeRole.mutate({ roleId: id })} />
                    </span>
                    <textarea className="snip-q-edit" rows={2} value={descDrafts[s.id] ?? s.description ?? ''}
                      onFocus={() => recordHistory(s.id)}
                      placeholder="Describe this snippet…" aria-label="Snippet description" maxLength={DESCRIPTION_MAX_CHARS} disabled={s.excluded} onKeyDown={commitOnEnter}
                      onChange={(e) => {
                        const v = e.target.value;
                        setDescDrafts((p) => ({ ...p, [s.id]: v }));
                        if (descTimer.current) clearTimeout(descTimer.current);
                        descTimer.current = setTimeout(() => update.mutate({ snippetId: s.id, description: v || null }), AUTOSAVE_DEBOUNCE_MS);
                      }}
                      onBlur={() => {
                        const v = descDrafts[s.id];
                        if (v !== undefined && v !== (s.description ?? '')) update.mutate({ snippetId: s.id, description: v || null });
                      }} />
                    </span>
                    <span className="snip-conf">
                      <span className="chip mono time-pill">{fmtTime(s.inMs, false)} – {fmtTime(s.outMs, false)}</span>
                      {composer.inDraft(s.id) && <SentBadge />}
                      {s.muted && <span className="sub conf-mini" title="This snippet's audio is muted by default">muted</span>}
                      {s.id === selected && staged ? <span className="chip acc">adjusting</span>
                        : s.source === 'manual' ? null
                          : (
                            <span className={`conf-shield ${s.confidence >= 0.85 ? 'cs-blue' : s.confidence >= 0.75 ? 'cs-purple' : s.confidence > 0.55 ? 'cs-orange' : 'cs-red'}`}
                              title={`AI confidence ${Math.round(s.confidence * 100)}%`}>
                              <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 2.2l4.6 1.7v3.5c0 3-2 5.1-4.6 6-2.6-.9-4.6-3-4.6-6V3.9z" /></svg>
                            </span>
                          )}
                      {starsOf('snippet', s.id)}
                      <RowMenu items={[
                        ...rateItems('snippet', s.id),
                        ...(s.source === 'manual' ? [] : [{ label: `Confidence: ${Math.round(s.confidence * 100)}%`, disabled: true, onClick: () => undefined }, null]),
                        ...composer.snippetMenuItems(s.id),
                        null,
                        { label: 'Play this snippet', onClick: () => selectSnippet(s.id) },
                        { label: 'Adjust start and end points', onClick: () => selectSnippet(s.id) },
                        null,
                        { label: 'Lengthen to the next sensible point', onClick: () => { recordHistory(s.id); nudge.mutate({ snippetId: s.id, direction: 'longer' }); } },
                        { label: 'Shorten to the previous sensible point', onClick: () => { recordHistory(s.id); nudge.mutate({ snippetId: s.id, direction: 'shorter' }); } },
                        mergeItem,
                        null,
                        { label: s.muted ? 'Unmute this snippet\'s audio' : 'Mute this snippet\'s audio', onClick: () => { recordHistory(s.id); update.mutate({ snippetId: s.id, muted: !s.muted }); } },
                        { label: 'Switch off', onClick: () => { recordHistory(s.id); update.mutate({ snippetId: s.id, excluded: true }); } },
                        null,
                        { label: 'Tag this clip…', onClick: () => setTagging(true) },
                        { label: 'Open in tagging', onClick: () => openInTagging({ snippet: s.id }) },
                      ]} />
                    </span>
                  </div>
                ))}
                <button type="button" className="btn snips-add" disabled={addSnippet.isPending} onClick={() => addRow('highlight')}><Icon name="plus" /> Add highlight</button>
              </div>
              )}
              {!analysing && <div className="row snips-autofoot"><AutoPill>Auto · {tabKept} kept, {tabRows.length - tabKept} excluded</AutoPill></div>}
              <div className="sub snips-foot">Click a row to play and zoom it · space plays and pauses</div>
              <div className="sub snips-foot">Every range is an index on the original file. Nothing is cut out of it. An excluded row keeps its place; bringing it back gives it a fresh colour.</div>
              {(addSnippet.isError || splitSnippet.isError || mergeSnippets.isError || updatePhrase.isError || saveSound.isError || removeSound.isError || shareSounds.isError) && (
                <div className="error" role="alert">{addSnippet.error?.message ?? splitSnippet.error?.message ?? mergeSnippets.error?.message ?? updatePhrase.error?.message ?? saveSound.error?.message ?? removeSound.error?.message ?? shareSounds.error?.message}</div>
              )}
              {remixOpen && (
                <RemixModal wide={Boolean(d.width && d.height && d.width > d.height)} pending={remix.isPending} error={remix.error?.message ?? null}
                  onPick={(target) => remix.mutate({ assetId: d.id, from: remixFrom, format: aspect, target })} onClose={() => setRemixOpen(false)} />
              )}
              {(update.isError || confirm.isError) && <div className="card pad error" role="alert">{update.error?.message ?? confirm.error?.message}</div>}
            </div>
          </>
        )}
      </section>
    </>
  );
}

/**
 * The Analyse toolbar: compact, icon-driven, always visible for the open asset. Constraints grey out
 * what falls outside them; re-analysis replaces every proposal.
 */
/** A slider's shape: positions run 0..1 in `steps` notches; toVal and toPos map value to track and back. */
interface SliderScale { steps: number; min: number; max: number; toVal: (t: number) => number; toPos: (v: number) => number }

/** Snippet counts: linear 1..5, then the log-spaced ladder up to 30 the toolbar promises. */
const EXTRACT_STOPS = [1, 2, 3, 4, 5, 6, 8, 10, 14, 20, 30];
const EXTRACT_SCALE: SliderScale = {
  steps: EXTRACT_STOPS.length - 1,
  min: EXTRACT_STOPS[0]!,
  max: EXTRACT_STOPS[EXTRACT_STOPS.length - 1]!,
  toVal: (t) => EXTRACT_STOPS[Math.round(t * (EXTRACT_STOPS.length - 1))]!,
  toPos: (v) => {
    let best = 0;
    for (let i = 1; i < EXTRACT_STOPS.length; i++) if (Math.abs(EXTRACT_STOPS[i]! - v) < Math.abs(EXTRACT_STOPS[best]! - v)) best = i;
    return best / (EXTRACT_STOPS.length - 1);
  },
};

/** Seconds: 0.1 to 2 across the first half, 2 to 7 to three quarters, then logarithmic out to 60. */
const DUR_HALF_S = 2;
const DUR_KNEE_S = 7;
const DUR_MAX_S = 60;
const DUR_MIN_S = 0.1;
const DURATION_SCALE: SliderScale = {
  steps: 200,
  min: DUR_MIN_S,
  max: DUR_MAX_S,
  toVal: (t) => (t <= 0.5 ? DUR_MIN_S + (t / 0.5) * (DUR_HALF_S - DUR_MIN_S)
    : t <= 0.75 ? DUR_HALF_S + ((t - 0.5) / 0.25) * (DUR_KNEE_S - DUR_HALF_S)
      : DUR_KNEE_S * Math.pow(DUR_MAX_S / DUR_KNEE_S, (t - 0.75) / 0.25)),
  toPos: (v) => (v <= DUR_HALF_S ? ((v - DUR_MIN_S) / (DUR_HALF_S - DUR_MIN_S)) * 0.5
    : v <= DUR_KNEE_S ? 0.5 + ((v - DUR_HALF_S) / (DUR_KNEE_S - DUR_HALF_S)) * 0.25
      : 0.75 + 0.25 * (Math.log(v / DUR_KNEE_S) / Math.log(DUR_MAX_S / DUR_KNEE_S))),
};

/**
 * The clean dual-range control. Line markers instead of bubbles; blue between them, grey outside;
 * the two markers may sit on the same notch to force an exact value. The numbers under each end
 * line up with the other tools' labels; double-clicking one opens an inline fine editor with
 * minus/plus and direct typing. The scale itself is nonlinear, defined by the SliderScale.
 */
function RangeSliderTool({ minVal, maxVal, scale, integer, onChange, ariaPrefix }: {
  minVal: number | null;
  maxVal: number | null;
  scale: SliderScale;
  integer?: boolean;
  onChange: (min: number | null, max: number | null) => void;
  ariaPrefix: string;
}) {
  const [editing, setEditing] = useState<'min' | 'max' | null>(null);
  const trackRef = useRef<HTMLSpanElement>(null);
  const clampScale = (v: number) => Math.max(scale.min, Math.min(v, scale.max));
  const lo = clampScale(minVal ?? scale.min);
  const hi = clampScale(maxVal ?? scale.max);
  const loPos = scale.toPos(lo);
  const hiPos = scale.toPos(Math.max(hi, lo));
  useEffect(() => {
    trackRef.current?.style.setProperty('--lo', `${(loPos * 100).toFixed(1)}%`);
    trackRef.current?.style.setProperty('--hi', `${(hiPos * 100).toFixed(1)}%`);
  }, [loPos, hiPos]);
  const norm = (v: number) => (integer ? Math.round(v) : Math.round(v * 10) / 10);
  const fmt = (v: number) => (integer ? String(Math.round(v)) : norm(v).toFixed(1));
  const commit = (a: number, b: number) => onChange(a <= scale.min ? null : norm(a), b >= scale.max ? null : norm(b));
  const typed = (side: 'min' | 'max', raw: string) => {
    const v = Number(raw);
    if (!Number.isFinite(v)) return;
    if (side === 'min') commit(Math.min(clampScale(v), hi), hi);
    else commit(lo, Math.max(clampScale(v), lo));
  };
  // In-place editing: the number stays exactly where it is, at the same size, and simply accepts keys.
  const fineEditor = (side: 'min' | 'max') => (
    <input autoFocus type="text" inputMode="decimal" className="tool-label range-num mono range-edit"
      defaultValue={fmt(side === 'min' ? lo : hi)} aria-label={`${ariaPrefix} ${side}`}
      onFocus={(e) => e.currentTarget.select()}
      onBlur={(e) => { typed(side, e.target.value); setEditing(null); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { typed(side, e.currentTarget.value); setEditing(null); }
        if (e.key === 'Escape') setEditing(null);
      }} />
  );
  return (
    <span className="seg-length-mid">
      <span className="row seg-length-foot">
        <span className="row seg-length-end" onDoubleClick={() => setEditing('min')} title="Double-click to fine-tune">
          {editing === 'min' ? fineEditor('min') : <span className="tool-label range-num mono">{fmt(lo)}</span>}
        </span>
        <span className="row seg-length-end" onDoubleClick={() => setEditing('max')} title="Double-click to fine-tune">
          {editing === 'max' ? fineEditor('max') : <span className="tool-label range-num mono">{fmt(hi)}</span>}
        </span>
      </span>
      <span className="dual-range seg-length-range" ref={trackRef}>
        <input type="range" min={0} max={scale.steps} step={1} value={Math.round(loPos * scale.steps)} aria-label={`Minimum ${ariaPrefix}`}
          onChange={(e) => commit(Math.min(norm(scale.toVal(Number(e.target.value) / scale.steps)), hi), hi)} />
        <input type="range" min={0} max={scale.steps} step={1} value={Math.round(hiPos * scale.steps)} aria-label={`Maximum ${ariaPrefix}`}
          onChange={(e) => commit(lo, Math.max(norm(scale.toVal(Number(e.target.value) / scale.steps)), lo))} />
      </span>
    </span>
  );
}

interface PanelActions {
  upload: () => void;
  load: () => void;
  next: () => void;
  close: () => void;
  composer: () => void;
  composerDisabled: boolean;
  /** Null once indexed; Save and index is the hand-off into tagging. */
  save: (() => void) | null;
  savePending: boolean;
  tag: () => void;
}

function AnalysePanel({ assetId, durationMs, stored, segmented, history, onRecent, onQueued, actions }: {
  assetId: string;
  durationMs: number;
  stored: AnalyseParams;
  /** Already segmented: a repeat automatic pass asks the AI for a fresh angle. */
  segmented: boolean;
  history: { undo: () => void; redo: () => void; canUndo: boolean; canRedo: boolean };
  onRecent: () => void;
  onQueued: () => Promise<void>;
  actions: PanelActions;
}) {
  const [describeOpen, setDescribeOpen] = useState(false);
  const [describeText, setDescribeText] = useState('');
  const [params, setParams] = useState<AnalyseParams>(stored);
  useEffect(() => setParams(stored), [assetId, stored]);
  const reanalyse = useMutation(trpc.assets.reanalyse.mutationOptions({ onSuccess: onQueued }));
  const nudgeAll = useMutation(trpc.assets.nudgeAll.mutationOptions({ onSuccess: onQueued }));
  // Voice shows what the run will do: a long clip is transcribed only when its brand or this button says so.
  const transcribeLong = useQuery(trpc.brandSettings.analyseDefaults.queryOptions()).data?.explicit.transcribeLong ?? false;
  const voiceOn = transcribesSpeech(params.voice, durationMs, transcribeLong);
  return (
    <div className="analyse-toolbar-wrap">
      <Toolbar>
        <div className="tool-group">
          <span className="tool-group-title">File</span>
          <div className="row tool-group-row">
            <Tool icon="upload" label="Upload" title="Upload new clips" onClick={actions.upload} />
            <Tool icon="folder" label="Load" title="Pick a clip from the Library" onClick={actions.load} />
            <Tool icon="redo" label="Next" title="Open the next clip that needs work; falls back to the Library" onClick={actions.next} />
            <Tool icon="clock" label="Recent" title="Recently processed clips" onClick={onRecent} />
            <Tool icon="close" label="Close" title="Close this clip; everything is already saved" onClick={actions.close} />
          </div>
        </div>
        <ToolDivider />
        <div className="tool-group">
          <span className="tool-group-title">History</span>
          <div className="row tool-group-row">
            <Tool icon="undo" label="Undo" title="Undo the last snippet edit" disabled={!history.canUndo} onClick={history.undo} />
            <Tool icon="redo-arrow" label="Redo" title="Redo the undone edit" disabled={!history.canRedo} onClick={history.redo} />
          </div>
        </div>
        <ToolDivider />
        <div className="tool-group">
          <span className="tool-group-title">Snippets to extract</span>
          <RangeSliderTool integer scale={EXTRACT_SCALE} ariaPrefix="snippets to extract"
            minVal={params.minSnippets} maxVal={params.maxSnippets}
            onChange={(a, b) => setParams({ ...params, minSnippets: a, maxSnippets: b })} />
        </div>
        <ToolDivider />
        <div className="tool-group">
          <span className="tool-group-title">Snippet duration</span>
          <RangeSliderTool scale={DURATION_SCALE} ariaPrefix="snippet seconds"
            minVal={params.minSnippetMs !== null ? params.minSnippetMs / 1000 : null}
            maxVal={params.maxSnippetMs !== null ? params.maxSnippetMs / 1000 : null}
            onChange={(a, b) => setParams({ ...params, minSnippetMs: a === null ? null : Math.round(a * 1000), maxSnippetMs: b === null ? null : Math.round(b * 1000) })} />
        </div>
        <ToolDivider />
        <div className="tool-group">
          <span className="tool-group-title">Adjust all</span>
          <div className="row tool-group-row">
            <Tool icon="chevs-up" label="Longer" title="Lengthen every clip to its next sensible point" disabled={nudgeAll.isPending}
              onClick={() => nudgeAll.mutate({ assetId, direction: 'longer' })} />
            <Tool icon="chevs-down" label="Shorter" title="Shorten every clip to its previous sensible point" disabled={nudgeAll.isPending}
              onClick={() => nudgeAll.mutate({ assetId, direction: 'shorter' })} />
          </div>
        </div>
        <ToolDivider />
        <div className="tool-group">
          <span className="tool-group-title">Options</span>
          <div className="row tool-group-row">
            <Tool icon="wave" label="Voice" title={voiceOn ? 'Voice: transcribe speech (click to skip)' : 'Voice: no transcription (click to transcribe)'}
              active={voiceOn} onClick={() => setParams({ ...params, voice: voiceOn ? 'none' : 'always' })} />
            <ToolField labelBelow plain label="Sens." value={params.sceneThreshold} placeholder="0.3" min={0.1} max={0.9} step={0.05} base={0.3} width="narrow"
              onChange={(v) => setParams({ ...params, sceneThreshold: v })} />
            <Tool icon="wand" label="Style" title="Cut style presets, coming soon" disabled onClick={() => undefined} />
            <Tool icon="grid" label="Frames" title="Frame-accurate cuts, coming soon" disabled onClick={() => undefined} />
          </div>
        </div>
        <ToolDivider />
        <div className="tool-group">
          <span className="tool-group-title">Process clip</span>
          <div className="row tool-group-row">
            <Tool icon="scissors" label="Split" title="Free mechanical split: scene cuts and your settings segment the clip, no AI; classifiers and descriptions stay yours to fill" disabled={reanalyse.isPending}
              onClick={() => reanalyse.mutate({ assetId, params: { ...params, ai: false, prompt: null, variation: false } })} />
            <Tool sparkle accent label="Prompt" title="Tell the AI what you want from this clip, or let it decide" disabled={reanalyse.isPending}
              onClick={() => setDescribeOpen(true)} />
            <Tool icon="eye" label="Re-analyse" title="The AI watches the clip again and rebuilds scenes, classifiers, descriptions and tags" disabled={reanalyse.isPending}
              onClick={() => reanalyse.mutate({ assetId, params: { ...params, ai: true, prompt: null, variation: segmented } })} />
          </div>
        </div>
        <ToolDivider />
        <div className="tool-group">
          <span className="tool-group-title">Export</span>
          <div className="row tool-group-row">
            {actions.save && (
              <Tool icon="save" label={actions.savePending ? 'Saving…' : 'Save'} title="Save and index every kept snippet"
                disabled={actions.savePending} onClick={actions.save} />
            )}
            <Tool icon="compose" label="Composer" title="Remix video: a new edit for a platform from the open tab's scenes or highlights"
              disabled={actions.composerDisabled} onClick={actions.composer} />
            <Tool icon="tag" label="Tag" title="Export the snippets to the tagging dashboard" onClick={actions.tag} />
          </div>
        </div>
      </Toolbar>
      {describeOpen && createPortal(
        <>
          <button type="button" className="overlay overlay-btn" aria-label="Close" onClick={() => setDescribeOpen(false)} />
          <div className="modal describe-modal" role="dialog" aria-label="Describe what you want">
            <h2><Spark className="spark" /> Describe what you want</h2>
            <textarea className="approve-caption" rows={3} autoFocus value={describeText} maxLength={300}
              placeholder="e.g. pull every moment where she tastes it, plus one clean packshot"
              aria-label="What should the AI look for"
              onChange={(e) => setDescribeText(e.target.value)} />
            <span className="sub">Or leave it blank and let the AI decide{segmented ? '; a repeat automatic pass looks for a fresh angle rather than the same result' : ''}.</span>
            <div className="row between">
              <button type="button" className="btn quiet" onClick={() => setDescribeOpen(false)}>Cancel</button>
              <button type="button" className="btn primary" disabled={reanalyse.isPending}
                onClick={() => {
                  const prompt = describeText.trim().length >= 3 ? describeText.trim() : null;
                  reanalyse.mutate({ assetId, params: { ...params, ai: true, prompt, variation: prompt ? false : segmented } });
                  setDescribeOpen(false);
                }}>
                {reanalyse.isPending ? 'Queuing…' : describeText.trim() ? 'Analyse' : 'Analyse automatically'}
              </button>
            </div>
          </div>
        </>,
        document.body,
      )}
      {reanalyse.isError && <div className="error" role="alert">{reanalyse.error.message}</div>}
    </div>
  );
}

/**
 * The analysing progress bar, driven by the worker: every stage it enters carries the share of the run
 * already done. No clock and no estimate, so a queued clip says so and a slow stage holds its place
 * instead of racing a guess to 95% and sitting there (Dax, 2026-09-23).
 */
export function AnalyseProgress({ stage, progress, etaAt, big }: { stage: string | null; progress: number | null; etaAt?: string | null | undefined; big?: boolean }) {
  if (stage === null) return <span className="sub stage-note">Queued</span>;
  // Broad time left (Dax, 2026-10-06), re-read on every poll of the row it sits in.
  const left = analyseTimeLeft(etaAt, Date.now());
  return (
    <span className={`analyse-progress${big ? ' big' : ''}`}>
      <progress className="upbar" value={Math.max(ANALYSE_PROGRESS_FLOOR, progress ?? 0)} max={1} aria-label="Analysis progress" />
      <span className="sub stage-note">{stage}{left ? ` · ${left}` : ''}</span>
    </span>
  );
}

function EdgeReadout({ k, ms, inWord }: { k: string; ms: number; inWord: boolean }) {
  return (
    <div className="inout">
      <span className="inout-k">{k}</span>
      <span className="mono">{fmtTime(ms)}</span>
      {inWord ? <span className="chip bad">in a word</span> : <span className="chip ok">clear of speech</span>}
    </div>
  );
}

/** The selection's signature colour as a thin full-width strip over the zoomed snippet block. */
function AccentBar({ colour }: { colour: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    canvas.width = 4;
    canvas.height = 4;
    ctx.fillStyle = colour;
    ctx.fillRect(0, 0, 4, 4);
  }, [colour]);
  return <canvas ref={ref} className="zoom-accent" aria-hidden="true" />;
}

/** A row's colour dot; with onToggle it is the row's on/off switch, hollow while the row is off. */
function MiniSwatch({ colour, off, onToggle }: { colour: string; off?: boolean; onToggle?: () => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.fillStyle = off ? OFF_ROW_COLOUR : colour;
    ctx.fillRect(0, 0, c.width, c.height);
    if (off) {
      ctx.clearRect(2, 2, c.width - 4, c.height - 4);
    }
  }, [colour, off]);
  const dot = <canvas ref={ref} className="swatch-dot" width={12} height={12} aria-hidden="true" />;
  if (!onToggle) return dot;
  return (
    <button type="button" className={`swatch-btn${off ? ' off' : ''}`} title={off ? 'Switch this snippet on' : 'Switch this snippet off'}
      aria-label={off ? 'Switch this snippet on' : 'Switch this snippet off'} aria-pressed={!off} onClick={onToggle}>
      {dot}
    </button>
  );
}

/** A row's merge tick: every tab joins its ticked rows the same way. */
function MergeTick({ id, ticked, onTick }: { id: string; ticked: boolean; onTick: (id: string, on: boolean) => void }) {
  return (
    <input type="checkbox" className="merge-check" aria-label="Tick this row to merge it with others"
      checked={ticked} onChange={(e) => onTick(id, e.target.checked)} />
  );
}
